import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Search, X, Grid3x3, List as ListIcon, Filter, ChevronDown, Settings,
  Copy, Pencil, Zap, ExternalLink, Trash2, ArrowUp, ArrowDown, Star, Plus,
  Layers,
} from "lucide-react";
import { toast } from "sonner";
import BulkEditModal from "./BulkEditModal";
import QuickEditModal from "./QuickEditModal";

/**
 * Etsy-style listing manager reused by Custom Products & Ready-Made tabs.
 *
 * Props:
 *   items                     — full array of loaded items (parent owns fetching)
 *   mode                      — 'custom' | 'ready-made' (controls fields / labels)
 *   pw                        — admin password (passed to bulk endpoints)
 *   onReload()                — refetch trigger for after mutations
 *   onEdit(item), onNew()     — open the parent's full editor for a single item
 *   updateItem(id, patch)     — single-item PATCH (used by quick-edit)
 *   deleteItem(id)            — single-item hard delete (used by gear menu)
 *   duplicateItem(id)         — single-item duplicate (POST /duplicate)
 *   bulkUpdate(payload)       — POST /admin/{...}/bulk-update
 *   bulkDelete(payload)       — POST /admin/{...}/bulk-delete
 *   thumbFor(item)            — return image URL for the card thumbnail
 *   priceOf(item)             — return numeric price
 *   viewUrl(item)             — public storefront URL (opens in new tab)
 *
 * Layout: [top controls] [content grid/list + right filters sidebar]
 * State persisted to localStorage per-`storageKey`.
 */
export default function AdminListingManager({
  items,
  loading = false,
  mode,
  storageKey,
  onReload,
  onEdit,
  onNew,
  updateItem,
  deleteItem,
  duplicateItem,
  bulkUpdate,
  bulkDelete,
  reorderItems,        // iter78 — POST /admin/{...}/reorder({ids})
  thumbFor,
  priceOf,
  viewUrl,
}) {
  // ---- Persistent filter/sort/view state ----
  const [q, setQ] = useState(() => localStorage.getItem(`${storageKey}_q`) || "");
  const [view, setView] = useState(() => localStorage.getItem(`${storageKey}_view`) || "grid");
  const [sort, setSort] = useState(() => localStorage.getItem(`${storageKey}_sort`) || "newest");
  const [pageSize, setPageSize] = useState(() => parseInt(localStorage.getItem(`${storageKey}_ps`) || "24", 10));
  const [page, setPage] = useState(1);

  // Filters (right sidebar)
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | inactive | archived | sold_out | draft
  const [categoryFilter, setCategoryFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("");      // "" | in_stock | low_stock | out_of_stock
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [customizableOnly, setCustomizableOnly] = useState(false);
  const [supplierFilter, setSupplierFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");        // "" | 7d | 30d | 90d
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAllMatching, setSelectAllMatching] = useState(false);

  // Modals
  const [bulkOpen, setBulkOpen] = useState(false);
  const [quickEditItem, setQuickEditItem] = useState(null);
  const [gearMenuFor, setGearMenuFor] = useState(null);

  // iter78 — reorder mode. When on: normal filters/pagination still work
  // but user can drag-drop cards. `reorderList` is the local order that
  // gets committed with "Save Order". `dragIndex` tracks the item being
  // dragged so we can shuffle on hover.
  const [reorderMode, setReorderMode] = useState(false);
  const [reorderList, setReorderList] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  // Persist state
  useEffect(() => { localStorage.setItem(`${storageKey}_q`, q); }, [q, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}_view`, view); }, [view, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}_sort`, sort); }, [sort, storageKey]);
  useEffect(() => { localStorage.setItem(`${storageKey}_ps`, String(pageSize)); }, [pageSize, storageKey]);

  // Close gear menu on outside click
  useEffect(() => {
    if (!gearMenuFor) return;
    const onClick = (e) => {
      if (!e.target.closest?.("[data-gear-menu]") && !e.target.closest?.("[data-gear-trigger]")) {
        setGearMenuFor(null);
      }
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [gearMenuFor]);

  // ---- Facet options ----
  const categoryOptions = useMemo(() => {
    const s = new Set();
    for (const p of items) if (p.category) s.add(p.category);
    return Array.from(s).sort();
  }, [items]);

  const supplierOptions = useMemo(() => {
    const s = new Set();
    for (const p of items) {
      const src = p.source || (p.source_data ? "external" : "manual");
      if (typeof src === "string" && src) s.add(src);
    }
    return Array.from(s).sort();
  }, [items]);

  // ---- Filter + search + sort pipeline ----
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const cutoffDays = dateFilter === "7d" ? 7 : dateFilter === "30d" ? 30 : dateFilter === "90d" ? 90 : null;
    const cutoffMs = cutoffDays ? Date.now() - cutoffDays * 86400000 : null;

    return items.filter((p) => {
      // Status
      if (statusFilter === "active" && !p.active) return false;
      if (statusFilter === "inactive" && (p.active || p.archived)) return false;
      if (statusFilter === "archived" && !p.archived) return false;
      if (statusFilter === "sold_out" && p.stock_status !== "out_of_stock") return false;
      if (statusFilter === "draft" && !(p.source === "sanmar" && !p.active)) return false;
      // Category
      if (categoryFilter && p.category !== categoryFilter) return false;
      // Stock
      if (stockFilter && (p.stock_status || "in_stock") !== stockFilter) return false;
      // Price range
      const pr = priceOf(p);
      if (priceMin !== "" && pr < parseFloat(priceMin)) return false;
      if (priceMax !== "" && pr > parseFloat(priceMax)) return false;
      // Featured
      if (featuredOnly && !p.featured) return false;
      // Customizable (only meaningful in the ready-made view; ignored on custom)
      if (customizableOnly && !p.customizable) return false;
      // Supplier
      if (supplierFilter && (p.source || "manual") !== supplierFilter) return false;
      // Date
      if (cutoffMs) {
        const t = Date.parse(p.updated_at || p.created_at || "");
        if (isNaN(t) || t < cutoffMs) return false;
      }
      // Search
      if (needle) {
        const hay = [
          p.name, p.sku, p.brand, p.brand_line, p.model, p.slug,
          p.sanmar_style, p.id, p.category, p.description,
          ...(p.tags || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, q, statusFilter, categoryFilter, stockFilter, priceMin, priceMax,
      featuredOnly, customizableOnly, supplierFilter, dateFilter, priceOf]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const cmpDate = (a, b, key) => (Date.parse(b[key] || "") || 0) - (Date.parse(a[key] || "") || 0);
    switch (sort) {
      case "newest": arr.sort((a, b) => cmpDate(a, b, "created_at")); break;
      case "oldest": arr.sort((a, b) => -cmpDate(a, b, "created_at")); break;
      case "updated": arr.sort((a, b) => cmpDate(a, b, "updated_at")); break;
      case "price_hi": arr.sort((a, b) => priceOf(b) - priceOf(a)); break;
      case "price_lo": arr.sort((a, b) => priceOf(a) - priceOf(b)); break;
      case "az": arr.sort((a, b) => (a.name || "").localeCompare(b.name || "")); break;
      case "za": arr.sort((a, b) => (b.name || "").localeCompare(a.name || "")); break;
      case "stock_lo": arr.sort((a, b) => (a.stock ?? 0) - (b.stock ?? 0)); break;
      case "stock_hi": arr.sort((a, b) => (b.stock ?? 0) - (a.stock ?? 0)); break;
      default: break;
    }
    return arr;
  }, [filtered, sort, priceOf]);

  // ---- Pagination ----
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const pageStart = (page - 1) * pageSize;
  const pageItems = sorted.slice(pageStart, pageStart + pageSize);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);
  // Whenever filters change reset to page 1
  useEffect(() => { setPage(1); }, [q, statusFilter, categoryFilter, stockFilter, priceMin, priceMax,
      featuredOnly, customizableOnly, supplierFilter, dateFilter, sort]);

  // ---- Selection helpers ----
  const clearSelection = useCallback(() => { setSelectedIds([]); setSelectAllMatching(false); }, []);
  const toggleOne = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setSelectAllMatching(false);
  };
  const allOnPageSelected = pageItems.length > 0 && pageItems.every((p) => selectedIds.includes(p.id));
  const someOnPageSelected = pageItems.some((p) => selectedIds.includes(p.id));
  const toggleAllOnPage = () => {
    if (allOnPageSelected) {
      const pageIds = new Set(pageItems.map((p) => p.id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
      setSelectAllMatching(false);
    } else {
      const merged = Array.from(new Set([...selectedIds, ...pageItems.map((p) => p.id)]));
      setSelectedIds(merged);
    }
  };
  const selectAllMatchingIds = () => {
    setSelectedIds(sorted.map((p) => p.id));
    setSelectAllMatching(true);
  };

  const effectiveSelectedIds = selectAllMatching ? sorted.map((p) => p.id) : selectedIds;

  // ---- Bulk mutations ----
  const runBulkUpdate = async (payload) => {
    const res = await bulkUpdate(payload);
    toast.success(`Updated ${res.modified ?? res.processed ?? 0} product(s)`);
    clearSelection();
    onReload();
  };
  const runBulkActivate = async (active) => {
    if (effectiveSelectedIds.length === 0) return;
    await runBulkUpdate({ ids: effectiveSelectedIds, set: { active } });
  };
  const runBulkArchive = async () => {
    if (effectiveSelectedIds.length === 0) return;
    if (!window.confirm(`Archive ${effectiveSelectedIds.length} product(s)? They will be hidden from the storefront but recoverable.`)) return;
    const res = await bulkDelete({ ids: effectiveSelectedIds });
    toast.success(`Archived ${res.archived ?? 0} product(s)`);
    clearSelection();
    onReload();
  };
  const runBulkDelete = async () => {
    if (effectiveSelectedIds.length === 0) return;
    if (!window.confirm(`⚠️ PERMANENTLY DELETE ${effectiveSelectedIds.length} product(s)? This CANNOT be undone. Consider Archive instead.`)) return;
    if (!window.confirm(`Really delete permanently? Type OK on the next prompt if you're sure.`)) return;
    if (!window.prompt(`Type DELETE to confirm hard-delete of ${effectiveSelectedIds.length} product(s):`)?.toUpperCase() === "DELETE") return;
    const res = await bulkDelete({ ids: effectiveSelectedIds, hard: true });
    toast.success(`Deleted ${res.hard_deleted ?? 0} product(s)`);
    clearSelection();
    onReload();
  };

  const hasFilters = q || statusFilter !== "all" || categoryFilter || stockFilter ||
    priceMin || priceMax || featuredOnly || customizableOnly || supplierFilter || dateFilter;
  const clearFilters = () => {
    setQ(""); setStatusFilter("all"); setCategoryFilter(""); setStockFilter("");
    setPriceMin(""); setPriceMax(""); setFeaturedOnly(false); setCustomizableOnly(false);
    setSupplierFilter(""); setDateFilter("");
  };

  // ---- Reorder mode helpers (iter78, pointer-events impl) ----
  const enterReorderMode = () => {
    const sorted = [...items]
      .filter((p) => p.active && !p.archived)
      .sort((a, b) => {
        const fa = a.featured ? 0 : 1, fb = b.featured ? 0 : 1;
        if (fa !== fb) return fa - fb;
        return (a.featured_order ?? 9999) - (b.featured_order ?? 9999);
      });
    setReorderList(sorted);
    setReorderMode(true);
    setSelectedIds([]);
  };
  const exitReorderMode = () => {
    setReorderMode(false);
    setReorderList([]);
    setDragIndex(null);
  };
  // Pointer-event based drag. HTML5 native `draggable` was inconsistent
  // across browsers/touchpads and image children hijacked the drag.
  // Instead: on pointerdown we grab the card, on pointermove we hit-test
  // via document.elementFromPoint() to find the card under the cursor
  // and splice-swap. Works desktop + touch + all browsers.
  const dragMoveRef = useRef(null);
  const onPointerDownCard = (index) => (e) => {
    if (e.button !== undefined && e.button !== 0) return;  // ignore right-click
    e.preventDefault();
    setDragIndex(index);
    // Track live index via ref so mousemove closure sees latest value.
    dragMoveRef.current = index;
    const handleMove = (ev) => {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const y = ev.touches ? ev.touches[0].clientY : ev.clientY;
      const target = document.elementFromPoint(x, y);
      const card = target && target.closest && target.closest('[data-testid^="reorder-card-"]');
      if (!card) return;
      const idAttr = card.getAttribute("data-testid").replace("reorder-card-", "");
      setReorderList((prev) => {
        const from = dragMoveRef.current;
        const to = prev.findIndex((p) => p.id === idAttr);
        if (from === null || to === -1 || from === to) return prev;
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        dragMoveRef.current = to;
        setDragIndex(to);
        return next;
      });
    };
    const handleUp = () => {
      setDragIndex(null);
      dragMoveRef.current = null;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);
  };
  const saveOrder = async () => {
    if (!reorderItems) { toast.error("Reorder not available"); return; }
    setSavingOrder(true);
    try {
      const ids = reorderList.map((p) => p.id);
      await reorderItems(ids);
      toast.success(`Saved order for ${ids.length} products`);
      exitReorderMode();
      onReload();
    } catch (e) {
      toast.error("Save failed: " + (e?.message || "unknown"));
    } finally {
      setSavingOrder(false);
    }
  };

  // ---- Badge / featured helpers (iter78c) ----
  const BADGES = [
    { key: "bestseller", label: "🔥 Best Seller" },
    { key: "new-drop", label: "✨ New Drop" },
    { key: "trending", label: "📈 Trending" },
  ];
  const toggleBadge = async (p, badgeKey) => {
    const current = Array.isArray(p.badges) ? p.badges : [];
    const next = current.includes(badgeKey)
      ? current.filter((b) => b !== badgeKey)
      : [...current, badgeKey];
    try {
      await updateItem(p.id, { badges: next });
      toast.success(current.includes(badgeKey) ? "Badge removed" : "Badge added");
      onReload();
    } catch { toast.error("Update failed"); }
  };
  const toggleFeatured = async (p) => {
    try {
      await updateItem(p.id, { featured: !p.featured });
      toast.success(p.featured ? "Unmarked featured" : "Marked featured");
      onReload();
    } catch { toast.error("Update failed"); }
  };
  const setFeaturedOrder = async (p, val) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n === (p.featured_order ?? 9999)) return;
    try {
      await updateItem(p.id, { featured_order: n });
      onReload();
    } catch { toast.error("Update failed"); }
  };

  // ---- Card render ----
  const renderCard = (p) => {
    const price = priceOf(p);
    const isSelected = selectedIds.includes(p.id) || selectAllMatching;
    return (
      <div
        key={p.id}
        data-testid={`listing-card-${p.id}`}
        className={`relative surface-card overflow-hidden transition-all ${
          isSelected ? "ring-2 ring-[var(--tc-red)]" : ""
        } ${(!p.active || p.archived) ? "opacity-60" : ""}`}
      >
        {/* Corner: checkbox + gear */}
        <div className="absolute top-2 left-2 z-10">
          <label className="flex items-center justify-center w-6 h-6 bg-black/80 border border-[#333] cursor-pointer hover:border-white">
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggleOne(p.id)}
              data-testid={`listing-checkbox-${p.id}`}
              className="accent-[var(--tc-red)]"
            />
          </label>
        </div>
        <div className="absolute top-2 right-2 z-10">
          <button
            data-gear-trigger
            data-testid={`gear-menu-${p.id}`}
            onClick={(e) => { e.stopPropagation(); setGearMenuFor(gearMenuFor === p.id ? null : p.id); }}
            className="w-8 h-8 flex items-center justify-center bg-black/80 border border-[#333] hover:border-white text-zinc-400 hover:text-white"
            aria-label="Actions"
          >
            <Settings size={14} />
          </button>
          {gearMenuFor === p.id && (
            <div
              data-gear-menu
              className="absolute right-0 mt-1 w-52 bg-[#0A0A0A] border border-[#222] shadow-2xl py-1"
              onClick={(e) => e.stopPropagation()}
            >
              <MenuBtn icon={Zap} onClick={() => { setQuickEditItem(p); setGearMenuFor(null); }} testId={`menu-quick-${p.id}`}>Quick edit</MenuBtn>
              <MenuBtn icon={Pencil} onClick={() => { onEdit(p); setGearMenuFor(null); }} testId={`menu-edit-${p.id}`}>Full edit</MenuBtn>
              <MenuBtn icon={ExternalLink} onClick={() => { window.open(viewUrl(p), "_blank"); setGearMenuFor(null); }} testId={`menu-view-${p.id}`}>View product</MenuBtn>
              <MenuBtn icon={Copy} onClick={async () => {
                setGearMenuFor(null);
                try { await duplicateItem(p.id); toast.success("Duplicated"); onReload(); } catch { toast.error("Duplicate failed"); }
              }} testId={`menu-dup-${p.id}`}>Duplicate</MenuBtn>
              <div className="border-t border-[#222] my-1" />
              <div className="px-3 pt-1 pb-1 font-mono text-[9px] tracking-widest text-zinc-500">MARK AS…</div>
              <MenuBtn
                icon={Star}
                onClick={() => { toggleFeatured(p); setGearMenuFor(null); }}
                testId={`menu-mark-featured-${p.id}`}
              >
                {p.featured ? "✓ Featured" : "Featured"}
              </MenuBtn>
              {BADGES.map((b) => {
                const on = (p.badges || []).includes(b.key);
                return (
                  <MenuBtn
                    key={b.key}
                    icon={Star}
                    onClick={() => { toggleBadge(p, b.key); setGearMenuFor(null); }}
                    testId={`menu-mark-${b.key}-${p.id}`}
                  >
                    {on ? `✓ ${b.label}` : b.label}
                  </MenuBtn>
                );
              })}
              <div className="border-t border-[#222] my-1" />
              <MenuBtn icon={Trash2} danger onClick={async () => {
                setGearMenuFor(null);
                if (!window.confirm("Delete this product? Consider archiving instead.")) return;
                try { await deleteItem(p.id); toast.success("Deleted"); onReload(); } catch { toast.error("Delete failed"); }
              }} testId={`menu-delete-${p.id}`}>Delete</MenuBtn>
            </div>
          )}
        </div>

        {/* Thumbnail */}
        <div className="aspect-square bg-black overflow-hidden">
          {thumbFor(p) ? (
            <img
              src={thumbFor(p)}
              alt={p.name}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs font-mono">NO IMAGE</div>
          )}
          {p.featured && (
            <div className="absolute top-2 left-1/2 -translate-x-1/2 chip border-yellow-500 text-yellow-400" title="Featured">
              <Star size={10} className="inline" /> FEATURED
            </div>
          )}
          {(p.badges || []).length > 0 && (
            <div className="absolute top-9 left-1/2 -translate-x-1/2 flex gap-1 flex-wrap justify-center max-w-[85%]">
              {(p.badges || []).includes("bestseller") && (
                <span className="chip border-orange-500 bg-black/80 backdrop-blur text-orange-400">🔥 BEST SELLER</span>
              )}
              {(p.badges || []).includes("new-drop") && (
                <span className="chip border-emerald-500 bg-black/80 backdrop-blur text-emerald-400">✨ NEW DROP</span>
              )}
              {(p.badges || []).includes("trending") && (
                <span className="chip border-pink-500 bg-black/80 backdrop-blur text-pink-400">📈 TRENDING</span>
              )}
            </div>
          )}
          {p.bestseller_rank && !(p.badges || []).includes("bestseller") && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 chip border-orange-500 bg-black/80 text-orange-400 backdrop-blur" title={`Best seller #${p.bestseller_rank}`}>
              🔥 BEST SELLER #{p.bestseller_rank}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="p-3">
          <div className="font-ui font-bold uppercase text-sm truncate" title={p.name}>{p.name || "Untitled"}</div>
          <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-0.5 truncate">
            {p.sku || p.id}
          </div>
          <div className="flex items-center justify-between mt-2">
            <div className="font-mono text-[var(--tc-red)] text-sm font-bold">
              ${price.toFixed(2)}
              {p.sale_price != null && p.sale_price < price && (
                <span className="ml-2 text-[10px] line-through text-zinc-500">${(p.sale_price).toFixed(2)}</span>
              )}
            </div>
            <StatusPill p={p} />
          </div>
          <div className="mt-2 flex items-center justify-between font-mono text-[9px] tracking-widest text-zinc-500">
            <span className="truncate">{p.category || "—"}</span>
            <span>{formatDate(p.updated_at || p.created_at)}</span>
          </div>
          <div className="mt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <span className="font-mono text-[9px] tracking-widest text-zinc-500">FEATURED #</span>
            <input
              type="number"
              defaultValue={p.featured_order ?? 9999}
              onBlur={(e) => setFeaturedOrder(p, e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
              data-testid={`featured-order-${p.id}`}
              className="w-16 bg-black border border-[#222] px-2 py-0.5 font-mono text-[11px] outline-none focus:border-[var(--tc-red)]"
              title="Lower = higher on storefront listing. e.g. 0 for top."
            />
          </div>
        </div>
      </div>
    );
  };

  const renderRow = (p) => {
    const price = priceOf(p);
    const isSelected = selectedIds.includes(p.id) || selectAllMatching;
    return (
      <tr
        key={p.id}
        data-testid={`listing-row-${p.id}`}
        className={`border-b border-[#222] ${isSelected ? "bg-[#141414]" : ""}`}
      >
        <td className="p-2">
          <input
            type="checkbox"
            checked={isSelected}
            onChange={() => toggleOne(p.id)}
            data-testid={`listing-checkbox-${p.id}`}
            className="accent-[var(--tc-red)]"
          />
        </td>
        <td className="p-2 w-16">
          <img src={thumbFor(p)} alt="" className="w-12 h-12 object-cover bg-black" loading="lazy" />
        </td>
        <td className="p-2 font-ui text-xs uppercase truncate max-w-[220px]" title={p.name}>{p.name}</td>
        <td className="p-2 font-mono text-[10px] text-zinc-400">{p.sku || "—"}</td>
        <td className="p-2 font-mono text-xs text-zinc-400">{p.category || "—"}</td>
        <td className="p-2 font-mono text-[var(--tc-red)]">${price.toFixed(2)}</td>
        <td className="p-2 font-mono text-[10px] text-zinc-400">{p.stock_status || "in_stock"}</td>
        <td className="p-2"><StatusPill p={p} /></td>
        <td className="p-2">
          <input
            type="number"
            defaultValue={p.featured_order ?? 9999}
            onBlur={(e) => setFeaturedOrder(p, e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
            data-testid={`row-featured-order-${p.id}`}
            className="w-16 bg-black border border-[#222] px-2 py-0.5 font-mono text-[11px] outline-none focus:border-[var(--tc-red)]"
            title="Lower = higher on storefront"
          />
        </td>
        <td className="p-2 font-mono text-[10px] text-zinc-500">{formatDate(p.updated_at || p.created_at)}</td>
        <td className="p-2">
          <div className="flex gap-2">
            <button onClick={() => setQuickEditItem(p)} className="text-zinc-400 hover:text-white" title="Quick edit" data-testid={`row-quick-${p.id}`}><Zap size={14} /></button>
            <button onClick={() => onEdit(p)} className="text-zinc-400 hover:text-white" title="Full edit" data-testid={`row-edit-${p.id}`}><Pencil size={14} /></button>
            <button onClick={async () => {
              try { await duplicateItem(p.id); toast.success("Duplicated"); onReload(); } catch { toast.error("Duplicate failed"); }
            }} className="text-zinc-400 hover:text-white" title="Duplicate" data-testid={`row-dup-${p.id}`}><Copy size={14} /></button>
            <button onClick={async () => {
              if (!window.confirm("Delete this product?")) return;
              try { await deleteItem(p.id); toast.success("Deleted"); onReload(); } catch { toast.error("Delete failed"); }
            }} className="text-zinc-400 hover:text-[var(--tc-red)]" title="Delete" data-testid={`row-delete-${p.id}`}><Trash2 size={14} /></button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-4" data-testid={`listing-manager-${mode}`}>
      {/* ============= TOP CONTROLS ============= */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500" data-testid="listing-count">
          <span className="text-white">{sorted.length}</span> / {items.length} PRODUCTS
          {selectedIds.length > 0 && (
            <span className="ml-3 text-[var(--tc-red)]">· {selectedIds.length} SELECTED</span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {reorderItems && !reorderMode && (
            <button
              onClick={enterReorderMode}
              data-testid="listing-reorder-btn"
              className="px-4 py-2.5 border border-[#333] hover:border-white font-mono text-[10px] tracking-widest flex items-center gap-2"
              title="Drag-to-reorder featured products"
            >
              <Layers size={13} /> REORDER
            </button>
          )}
          <button
            onClick={onNew}
            data-testid="listing-new-btn"
            className="btn-tc-primary flex items-center gap-2 py-2.5 px-4"
          >
            <Plus size={14} /> New
          </button>
        </div>
      </div>

      {/* Reorder mode banner + save/cancel */}
      {reorderMode && (
        <div
          className="sticky top-2 z-30 border border-[var(--tc-red)] bg-[#0F0303] shadow-[0_8px_24px_-8px_rgba(255,0,0,0.35)] p-3 flex items-center gap-3 flex-wrap"
          data-testid="reorder-banner"
        >
          <div className="font-mono text-[10px] tracking-widest text-white">
            REORDER MODE · DRAG CARDS TO REARRANGE · {reorderList.length} ITEMS
          </div>
          <div className="flex-1" />
          <button
            onClick={saveOrder}
            disabled={savingOrder}
            data-testid="reorder-save"
            className="btn-tc-primary py-2 px-4 disabled:opacity-50"
          >
            {savingOrder ? "SAVING…" : "SAVE ORDER"}
          </button>
          <button
            onClick={exitReorderMode}
            data-testid="reorder-cancel"
            className="btn-tc-secondary py-2 px-4"
          >
            CANCEL
          </button>
        </div>
      )}

      {/* Source chips row (iter79c) — one-click quick-filter across sources.
          Counts derive from the loaded items so admins see how many of each
          are in the current dataset without opening the sidebar. */}
      {supplierOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="source-chip-row">
          <span className="font-mono text-[10px] tracking-widest text-zinc-500">/ SOURCE ·</span>
          <button
            onClick={() => setSupplierFilter("")}
            data-testid="source-chip-all"
            className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest ${
              !supplierFilter
                ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white"
                : "border-[#222] text-zinc-400 hover:border-zinc-500"
            }`}
          >
            ALL <span className="text-zinc-500">({items.length})</span>
          </button>
          {supplierOptions.map((src) => {
            const count = items.filter((p) => (p.source || "manual") === src).length;
            return (
              <button
                key={src}
                onClick={() => setSupplierFilter(src)}
                data-testid={`source-chip-${src}`}
                className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest ${
                  supplierFilter === src
                    ? "border-[var(--tc-red)] bg-[var(--tc-red)]/10 text-white"
                    : "border-[#222] text-zinc-400 hover:border-zinc-500"
                }`}
              >
                {src.toUpperCase()} <span className="text-zinc-500">({count})</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Search + Sort + View + PageSize row */}
      <div className="flex flex-wrap items-center gap-2 border border-[#1a1a1a] bg-[#0a0a0a] p-3">
        <div className="flex items-center gap-2 bg-black border border-[#222] px-3 py-2 flex-1 min-w-[260px]">
          <Search size={14} className="text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name / sku / category / tag / description…"
            className="bg-transparent outline-none text-sm font-body w-full"
            data-testid="listing-search"
          />
          {q && (
            <button onClick={() => setQ("")} className="text-zinc-500 hover:text-white" data-testid="listing-search-clear">
              <X size={12} />
            </button>
          )}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="bg-black border border-[#222] px-3 py-2 font-mono text-xs"
          data-testid="listing-sort"
        >
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="updated">Recently updated</option>
          <option value="price_hi">Price high → low</option>
          <option value="price_lo">Price low → high</option>
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
          <option value="stock_lo">Stock low → high</option>
          <option value="stock_hi">Stock high → low</option>
        </select>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          className="bg-black border border-[#222] px-3 py-2 font-mono text-xs"
          data-testid="listing-page-size"
        >
          <option value={24}>24 / page</option>
          <option value={48}>48 / page</option>
          <option value={96}>96 / page</option>
        </select>
        <div className="flex border border-[#222]">
          <button
            onClick={() => setView("grid")}
            data-testid="view-grid"
            className={`px-3 py-2 ${view === "grid" ? "bg-[var(--tc-red)]/20 text-white" : "text-zinc-500 hover:text-white"}`}
            title="Grid view"
          >
            <Grid3x3 size={14} />
          </button>
          <button
            onClick={() => setView("list")}
            data-testid="view-list"
            className={`px-3 py-2 ${view === "list" ? "bg-[var(--tc-red)]/20 text-white" : "text-zinc-500 hover:text-white"}`}
            title="List view"
          >
            <ListIcon size={14} />
          </button>
        </div>
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          data-testid="listing-toggle-filters"
          className={`px-3 py-2 border font-mono text-[10px] tracking-widest flex items-center gap-1 ${
            filtersOpen ? "border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400"
          }`}
        >
          <Filter size={12} /> FILTERS
        </button>
        {hasFilters && (
          <button
            onClick={clearFilters}
            data-testid="listing-clear-filters"
            className="px-3 py-2 border border-[#222] font-mono text-[10px] tracking-widest text-zinc-400 hover:text-white flex items-center gap-1"
          >
            <X size={11} /> CLEAR
          </button>
        )}
      </div>

      {/* ============= STICKY BULK BAR ============= */}
      {selectedIds.length > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="sticky top-2 z-30 border border-[var(--tc-red)] bg-[#0F0303] shadow-[0_8px_24px_-8px_rgba(255,0,0,0.35)] p-3 flex flex-wrap items-center gap-2"
        >
          <div className="font-mono text-[10px] tracking-widest text-white">
            {selectAllMatching ? `ALL ${sorted.length} MATCHING` : `${selectedIds.length} ON PAGE`} SELECTED
          </div>
          {!selectAllMatching && selectedIds.length < sorted.length && sorted.length > pageItems.length && (
            <button
              onClick={selectAllMatchingIds}
              className="font-mono text-[10px] tracking-widest underline text-white hover:no-underline"
              data-testid="bulk-select-all-matching"
            >
              Select all {sorted.length} matching products
            </button>
          )}
          <div className="flex-1" />
          <button onClick={() => runBulkActivate(true)}   data-testid="bulk-activate"   className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-green-700 text-green-400 hover:bg-green-950">ACTIVATE</button>
          <button onClick={() => runBulkActivate(false)}  data-testid="bulk-deactivate" className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-zinc-700 text-zinc-300 hover:bg-zinc-900">DEACTIVATE</button>
          <button onClick={() => setBulkOpen(true)}       data-testid="bulk-edit-btn"   className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-white text-white hover:bg-white/10">BULK EDIT</button>
          <button onClick={runBulkArchive}                data-testid="bulk-archive"    className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-amber-700 text-amber-400 hover:bg-amber-950">ARCHIVE</button>
          <button onClick={runBulkDelete}                 data-testid="bulk-delete"     className="font-mono text-[10px] tracking-widest px-3 py-1.5 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-red-950">DELETE</button>
          <button onClick={clearSelection}                data-testid="bulk-clear"      className="font-mono text-[10px] tracking-widest px-3 py-1.5 text-zinc-500 hover:text-white">CANCEL</button>
        </div>
      )}

      {/* ============= REORDER MODE VIEW ============= */}
      {reorderMode && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
          data-testid="reorder-grid"
        >
          {reorderList.map((p, index) => (
            <div
              key={p.id}
              onPointerDown={onPointerDownCard(index)}
              data-testid={`reorder-card-${p.id}`}
              style={{ touchAction: "none" }}
              className={`relative surface-card overflow-hidden cursor-move transition-all select-none ${
                dragIndex === index ? "opacity-40 scale-95 ring-2 ring-[var(--tc-red)]" : "hover:ring-2 hover:ring-[var(--tc-red)]"
              }`}
            >
              <div className="absolute top-2 left-2 z-10 w-8 h-8 flex items-center justify-center bg-[var(--tc-red)] text-white font-mono text-xs font-bold pointer-events-none">
                #{index + 1}
              </div>
              <div className="aspect-square bg-black">
                {thumbFor(p) && <img src={thumbFor(p)} alt={p.name} loading="lazy" draggable={false} className="w-full h-full object-cover pointer-events-none select-none" />}
              </div>
              <div className="p-3 pointer-events-none select-none">
                <div className="font-ui font-bold uppercase text-sm truncate">{p.name}</div>
                <div className="font-mono text-[10px] tracking-widest text-zinc-500 mt-0.5 truncate">{p.sku || p.id}</div>
                <div className="flex items-center gap-1 mt-1">
                  {p.featured && <span className="chip border-yellow-500 text-yellow-400"><Star size={9} className="inline" /> FEATURED</span>}
                  {p.bestseller_rank && <span className="chip border-orange-500 text-orange-400">🔥 #{p.bestseller_rank}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ============= CONTENT + SIDEBAR ============= */}
      {!reorderMode && (
      <div className={`grid gap-4 ${filtersOpen ? "lg:grid-cols-[1fr_260px]" : ""}`}>
        {/* Main content column */}
        <div>
          {/* Select-all-on-page */}
          <div className="flex items-center gap-3 mb-3 font-mono text-[10px] tracking-widest text-zinc-500">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allOnPageSelected}
                ref={(el) => { if (el) el.indeterminate = !allOnPageSelected && someOnPageSelected; }}
                onChange={toggleAllOnPage}
                data-testid="listing-select-all-page"
                className="accent-[var(--tc-red)]"
              />
              SELECT ALL ON PAGE
            </label>
            {sorted.length > pageSize && (
              <span className="text-zinc-600">
                · SHOWING {pageStart + 1}–{Math.min(pageStart + pageSize, sorted.length)} OF {sorted.length}
              </span>
            )}
          </div>

          {loading ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
              data-testid="listing-loading"
            >
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="surface-card overflow-hidden animate-pulse">
                  <div className="aspect-square bg-[#111]" />
                  <div className="p-3 space-y-2">
                    <div className="h-3 bg-[#111] w-3/4" />
                    <div className="h-2 bg-[#111] w-1/2" />
                    <div className="h-3 bg-[#111] w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="border border-[#222] bg-[#0a0a0a] p-10 text-center font-mono text-xs text-zinc-500" data-testid="listing-empty">
              {items.length === 0 ? "No products yet — click New to add one." : "No products match your filters."}
            </div>
          ) : view === "grid" ? (
            <div
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3"
              data-testid="listing-grid"
            >
              {pageItems.map(renderCard)}
            </div>
          ) : (
            <div className="surface-card overflow-x-auto" data-testid="listing-list-view">
              <table className="w-full text-left">
                <thead className="bg-[#050505] border-b border-[#222]">
                  <tr className="font-mono text-[10px] tracking-widest text-zinc-500">
                    <th className="p-2"></th>
                    <th className="p-2"></th>
                    <th className="p-2">NAME</th>
                    <th className="p-2">SKU</th>
                    <th className="p-2">CATEGORY</th>
                    <th className="p-2">PRICE</th>
                    <th className="p-2">STOCK</th>
                    <th className="p-2">STATUS</th>
                    <th className="p-2">FEAT #</th>
                    <th className="p-2">UPDATED</th>
                    <th className="p-2">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>{pageItems.map(renderRow)}</tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 font-mono text-[10px] tracking-widest" data-testid="listing-pagination">
              <div className="text-zinc-500">PAGE {page} / {totalPages}</div>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(Math.max(1, page - 1))}
                  className="px-3 py-1.5 border border-[#222] text-zinc-400 hover:text-white disabled:opacity-40 flex items-center gap-1"
                  data-testid="page-prev"
                >
                  <ArrowUp size={11} className="rotate-[-90deg]" /> PREV
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  className="px-3 py-1.5 border border-[#222] text-zinc-400 hover:text-white disabled:opacity-40 flex items-center gap-1"
                  data-testid="page-next"
                >
                  NEXT <ArrowDown size={11} className="rotate-[-90deg]" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ============= RIGHT FILTER SIDEBAR ============= */}
        {filtersOpen && (
          <aside
            className="border border-[#1a1a1a] bg-[#0a0a0a] p-4 h-fit lg:sticky lg:top-2 space-y-4"
            data-testid="listing-filters-sidebar"
          >
            <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ FILTERS</div>

            {/* Status */}
            <FilterGroup label="Listing status">
              {["all", "active", "inactive", "archived", "sold_out"].map((s) => (
                <label key={s} className="flex items-center gap-2 font-mono text-[11px] cursor-pointer">
                  <input
                    type="radio"
                    name="status"
                    checked={statusFilter === s}
                    onChange={() => setStatusFilter(s)}
                    data-testid={`filter-status-${s}`}
                    className="accent-[var(--tc-red)]"
                  />
                  {s === "all" ? "All" : s.replace("_", " ").toUpperCase()}
                </label>
              ))}
            </FilterGroup>

            {/* Category */}
            {categoryOptions.length > 0 && (
              <FilterGroup label="Category">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                  data-testid="filter-category"
                >
                  <option value="">All categories</option>
                  {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FilterGroup>
            )}

            {/* Stock */}
            <FilterGroup label="Stock status">
              <select
                value={stockFilter}
                onChange={(e) => setStockFilter(e.target.value)}
                className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                data-testid="filter-stock"
              >
                <option value="">Any</option>
                <option value="in_stock">In stock</option>
                <option value="low_stock">Low stock</option>
                <option value="out_of_stock">Out of stock</option>
              </select>
            </FilterGroup>

            {/* Price range */}
            <FilterGroup label="Price range">
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => setPriceMin(e.target.value)}
                  className="w-1/2 bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                  data-testid="filter-price-min"
                />
                <input
                  type="number"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => setPriceMax(e.target.value)}
                  className="w-1/2 bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                  data-testid="filter-price-max"
                />
              </div>
            </FilterGroup>

            {/* Featured only */}
            <FilterGroup label="Featured">
              <label className="flex items-center gap-2 font-mono text-[11px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={featuredOnly}
                  onChange={(e) => setFeaturedOnly(e.target.checked)}
                  data-testid="filter-featured"
                  className="accent-[var(--tc-red)]"
                />
                Featured only
              </label>
            </FilterGroup>

            {/* Supplier */}
            {supplierOptions.length > 1 && (
              <FilterGroup label="Supplier / brand">
                <select
                  value={supplierFilter}
                  onChange={(e) => setSupplierFilter(e.target.value)}
                  className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                  data-testid="filter-supplier"
                >
                  <option value="">All</option>
                  {supplierOptions.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
                </select>
              </FilterGroup>
            )}

            {/* Date */}
            <FilterGroup label="Updated within">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full bg-black border border-[#222] px-2 py-1.5 font-mono text-[11px]"
                data-testid="filter-date"
              >
                <option value="">Any time</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="90d">Last 90 days</option>
              </select>
            </FilterGroup>

            {hasFilters && (
              <button
                onClick={clearFilters}
                className="w-full font-mono text-[10px] tracking-widest px-3 py-2 border border-[#333] text-zinc-400 hover:text-[var(--tc-red)] hover:border-[var(--tc-red)]"
                data-testid="sidebar-clear-filters"
              >
                CLEAR ALL FILTERS
              </button>
            )}
          </aside>
        )}
      </div>
      )}

      {/* ============= MODALS ============= */}
      <BulkEditModal
        open={bulkOpen}
        selectedIds={effectiveSelectedIds}
        mode={mode}
        categoryOptions={categoryOptions}
        onClose={() => setBulkOpen(false)}
        onApply={runBulkUpdate}
      />
      <QuickEditModal
        open={!!quickEditItem}
        item={quickEditItem}
        mode={mode}
        onClose={() => setQuickEditItem(null)}
        onSave={async (patch) => {
          await updateItem(quickEditItem.id, patch);
          onReload();
        }}
      />
    </div>
  );
}

// ---- Local UI subcomponents ----
function MenuBtn({ icon: Icon, children, onClick, danger, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 w-full px-3 py-2 text-xs font-body text-left ${
        danger ? "text-[var(--tc-red)] hover:bg-red-950" : "text-zinc-200 hover:bg-[#141414]"
      }`}
    >
      <Icon size={13} /> {children}
    </button>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="space-y-2">
      <div className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function StatusPill({ p }) {
  if (p.archived) return <span className="chip border-zinc-800 text-zinc-500">ARCHIVED</span>;
  if (p.active) return <span className="chip border-green-700 text-green-400">ACTIVE</span>;
  return <span className="chip border-zinc-700 text-zinc-500">INACTIVE</span>;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  } catch { return "—"; }
}
